import React, { useState, useEffect, useRef } from 'react';
import { Card, Col, Row, Button, Table, Spinner, ProgressBar, Alert, Badge, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { LucideUploadCloud, LucideDownload, LucideCheckCircle, LucideAlertCircle, LucideUsers, LucideSearch } from 'lucide-react';
import { clientesImportacionService } from '../../../services/clientesImportacionService';

const ImportarClientes = () => {
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filasErrores, setFilasErrores] = useState([]);
    const [resumenImportacion, setResumenImportacion] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        cargarClientes();
    }, []);

    const cargarClientes = async () => {
        setLoading(true);
        try {
            const res = await clientesImportacionService.getClientes();
            if (res.success) {
                setClientes(res.data);
            }
        } catch (error) {
            toast.error('Error al cargar la lista de clientes.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const data = await clientesImportacionService.descargarPlantilla();
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'plantilla_clientes_avance.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            toast.success('Plantilla descargada con éxito.');
        } catch (error) {
            toast.error('Error al descargar la plantilla.');
            console.error(error);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            procesarArchivo(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            procesarArchivo(e.target.files[0]);
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const abrirModalCarga = () => {
        setShowUploadModal(true);
    };

    const cerrarModalCarga = () => {
        if (!uploading) {
            setShowUploadModal(false);
        }
    };

    const procesarArchivo = async (file) => {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (fileExtension !== 'xlsx' && fileExtension !== 'xls') {
            toast.error('Por favor, selecciona un archivo Excel (.xlsx o .xls).');
            return;
        }

        setUploading(true);
        setFilasErrores([]);
        setResumenImportacion(null);

        try {
            const res = await clientesImportacionService.importarExcel(file);
            if (res.success) {
                toast.success('Archivo importado exitosamente.');
                setResumenImportacion(res.data);
                if (res.data.errores && res.data.errores.length > 0) {
                    setFilasErrores(res.data.errores);
                    toast.warning(`Se importaron los datos con ${res.data.errores.length} observaciones.`);
                }
                cargarClientes();
                setShowUploadModal(false);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al procesar el archivo Excel.';
            toast.error(errorMsg);
            console.error(error);
        } finally {
            setUploading(false);
        }
    };

    const filteredClientes = clientes.filter(c =>
        c.cliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.codCliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.dni && c.dni.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.ruc && c.ruc.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.ddv && c.ddv.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="container-fluid py-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {/* Cabecera Premium */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h2 className="fw-bold mb-1" style={{ color: '#0f172a', letterSpacing: '-0.025em' }}>
                        Importación de clientes 
                    </h2>
                    <p className="text-muted mb-0">
                        Carga y actualiza cuotas, avances y datos de asesores para el Club de las Estrellas.
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-primary"
                        onClick={handleDownloadTemplate}
                        className="d-flex align-items-center gap-2 px-3 py-2 fw-semibold"
                        style={{ borderRadius: '10px', transition: 'all 0.2s' }}
                    >
                        <LucideDownload size={18} />
                        Descargar Plantilla
                    </Button>
                    <Button
                        variant="primary"
                        onClick={abrirModalCarga}
                        className="d-flex align-items-center gap-2 px-3 py-2 fw-semibold"
                        style={{ borderRadius: '10px', transition: 'all 0.2s', minWidth: '210px' }}
                        disabled={uploading}
                    >
                        <LucideUploadCloud size={18} />
                        {uploading ? 'Subiendo Base de Datos...' : 'Subir Base de Datos'}
                    </Button>
                </div>
            </div>

            <Row className="g-4">
                <Col xs={12}>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                        <Card.Header className="bg-white border-0 p-4 pb-0 d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <div className="p-2 rounded-lg" style={{ backgroundColor: '#e3f2e8', color: '#1f8a3f', borderRadius: '8px' }}>
                                    <LucideUsers size={20} color="#1f8a3f" />
                                </div>
                                <h5 className="fw-bold mb-0 text-dark">Base de Clientes ({filteredClientes.length})</h5>
                            </div>
                            
                            {/* Input de Búsqueda */}
                            <div className="position-relative" style={{ width: '280px' }}>
                                <Form.Control
                                    type="text"
                                    placeholder="Buscar cliente, DNI, RUC o asesor..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        paddingLeft: '36px',
                                        borderRadius: '10px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '0.9rem'
                                    }}
                                />
                                <LucideSearch size={16} className="position-absolute text-muted" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                            </div>
                        </Card.Header>
                        
                        <Card.Body className="p-4">
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-3 text-muted">Cargando base de datos...</p>
                                </div>
                            ) : filteredClientes.length === 0 ? (
                                <div className="text-center py-5 border border-dashed rounded" style={{ borderRadius: '12px' }}>
                                    <p className="text-muted mb-0">No se encontraron clientes registrados.</p>
                                </div>
                            ) : (
                                <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    <Table hover className="align-middle text-nowrap mb-0">
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 1, borderBottom: '2px solid #f1f5f9' }}>
                                            <tr className="text-muted" style={{ fontSize: '0.82rem' }}>
                                                <th>COD CLIENTE</th>
                                                <th>CLIENTE / DNI / RUC</th>
                                                <th>TELEFONO</th>
                                                <th>VENDEDOR / DDV</th>
                                                <th className="text-center">MES</th>
                                                <th>CUOTA / AVANCE (PACKS)</th>
                                                <th>PUNTOS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredClientes.map((c) => {
                                                const pct = c.cuotaTotalPacks > 0 ? Math.round((c.avanceTotalPacks / c.cuotaTotalPacks) * 100) : 0;
                                                return (
                                                    <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>{c.codCliente}</td>
                                                        <td>
                                                            <div className="fw-semibold text-dark mb-0" style={{ fontSize: '0.9rem' }}>{c.cliente}</div>
                                                            <div className="text-muted small">DNI: {c.dni || '-'}</div>
                                                            <div className="text-muted small">RUC: {c.ruc || '-'}</div>
                                                        </td>
                                                        <td style={{ fontSize: '0.85rem' }}>{c.telefono || '-'}</td>
                                                        <td>
                                                            <div className="fw-semibold text-dark mb-0" style={{ fontSize: '0.85rem' }}>{c.ddv || '-'}</div>
                                                            <span className="text-muted small">{c.telefonoVendedor ? `📞 ${c.telefonoVendedor}` : ''}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="light" text="dark" className="border px-2 py-1" style={{ fontSize: '0.78rem' }}>
                                                                {c.mesReferencia}
                                                            </Badge>
                                                        </td>
                                                        <td style={{ width: '220px' }}>
                                                            <div className="d-flex justify-content-between small text-dark mb-1">
                                                                <span><strong>{c.avanceTotalPacks}</strong> de {c.cuotaTotalPacks} packs</span>
                                                                <span><strong>{pct}%</strong></span>
                                                            </div>
                                                            <ProgressBar
                                                                now={Math.min(pct, 100)}
                                                                variant={pct >= 100 ? 'success' : pct >= 50 ? 'primary' : 'warning'}
                                                                style={{ height: '6px', borderRadius: '4px' }}
                                                            />
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="badge rounded-pill bg-warning text-dark fw-bold px-3 py-2" style={{ fontSize: '0.82rem' }}>
                                                                {c.puntos} pts
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showUploadModal} onHide={cerrarModalCarga} centered size="lg">
                <Modal.Header closeButton={!uploading} className="border-0 pb-0">
                    <Modal.Title className="fw-bold text-dark">Subir Base de Datos</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 pt-3">
                    <div className="mb-4">
                        <h5 className="fw-bold mb-2 text-dark">Base lista para importar</h5>
                        <p className="text-muted mb-0">
                            Usa este modal para cargar el Excel. La tabla se actualiza al terminar la importación y la plantilla ya contempla el campo <strong>DNI</strong>.
                        </p>
                    </div>

                    <div
                        onDragEnter={handleDrag}
                        onDragOver={handleDrag}
                        onDragLeave={handleDrag}
                        onDrop={handleDrop}
                        onClick={triggerFileInput}
                        style={{
                            border: dragActive ? '2px dashed #1f8a3f' : '2px dashed #cbd5e1',
                            borderRadius: '16px',
                            padding: '48px 24px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            background: dragActive ? 'linear-gradient(135deg, #eaf6ee 0%, #f1f8f3 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="d-none"
                            accept=".xlsx, .xls"
                            onChange={handleFileChange}
                        />

                        {uploading ? (
                            <div className="py-4">
                                <Spinner animation="border" variant="primary" className="mb-3" />
                                <p className="fw-semibold text-dark mb-1">Procesando Excel...</p>
                                <p className="text-muted small mb-0">Guardando datos de clientes, DNI, cuotas y avances...</p>
                            </div>
                        ) : (
                            <div>
                                <div className="d-inline-flex align-items-center justify-content-center mb-3" style={{ backgroundColor: '#d1fae5', color: '#0a7d33', width: '68px', height: '68px', borderRadius: '18px' }}>
                                    <LucideUploadCloud size={30} />
                                </div>
                                <p className="fw-semibold text-dark mb-1" style={{ fontSize: '1.05rem' }}>Arrastra tu archivo aquí</p>
                                <p className="text-muted small mb-3">o haz clic en este panel para importar tu base de datos</p>
                                <Badge bg="light" text="secondary" className="border px-3 py-2" style={{ borderRadius: '999px' }}>
                                    Soporta: .xlsx, .xls
                                </Badge>
                            </div>
                        )}
                    </div>

                    <div className="p-4 border mt-3" style={{ borderRadius: '16px', backgroundColor: '#f6fbf7' }}>
                        <h6 className="fw-bold mb-3 text-dark">Campos Soportados</h6>
                        <div className="d-flex flex-wrap gap-2">
                            <Badge bg="light" text="dark" className="border">cod_cliente (Obligatorio)</Badge>
                            <Badge bg="light" text="dark" className="border">cliente (Obligatorio)</Badge>
                            <Badge bg="light" text="dark" className="border">dni</Badge>
                            <Badge bg="light" text="dark" className="border">ruc</Badge>
                            <Badge bg="light" text="dark" className="border">telefono</Badge>
                            <Badge bg="light" text="dark" className="border">ddv</Badge>
                            <Badge bg="light" text="dark" className="border">telefono_vendedor</Badge>
                            <Badge bg="light" text="dark" className="border">cuota_total_packs</Badge>
                            <Badge bg="light" text="dark" className="border">cuota_retornable_cajas</Badge>
                            <Badge bg="light" text="dark" className="border">avance_total_packs</Badge>
                            <Badge bg="light" text="dark" className="border">avance_retornable_cajas</Badge>
                            <Badge bg="light" text="dark" className="border">comunicado_mes</Badge>
                            <Badge bg="light" text="dark" className="border">mes_referencia</Badge>
                        </div>
                    </div>

                    {resumenImportacion && (
                        <Alert variant="success" className="border-0 mt-3 mb-0 p-3" style={{ borderRadius: '16px', backgroundColor: '#ecfdf5' }}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <LucideCheckCircle size={18} className="text-success" />
                                <span className="fw-bold text-success">Resultados de Importación</span>
                            </div>
                            <div className="small text-dark">
                                <div>• Filas procesadas: <strong>{resumenImportacion.total}</strong></div>
                                <div>• Clientes nuevos: <strong>{resumenImportacion.creados}</strong></div>
                                <div>• Clientes actualizados: <strong>{resumenImportacion.actualizados}</strong></div>
                                {resumenImportacion.errores && resumenImportacion.errores.length > 0 && (
                                    <div className="text-danger mt-1">• Errores encontrados: <strong>{resumenImportacion.errores.length}</strong></div>
                                )}
                            </div>
                        </Alert>
                    )}

                    {filasErrores.length > 0 && (
                        <Alert variant="danger" className="border-0 mt-3 mb-0 p-3" style={{ borderRadius: '16px', backgroundColor: '#fef2f2', maxHeight: '220px', overflowY: 'auto' }}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <LucideAlertCircle size={18} className="text-danger" />
                                <span className="fw-bold text-danger">Observaciones de filas</span>
                            </div>
                            <div className="small text-danger" style={{ fontSize: '0.82rem' }}>
                                {filasErrores.map((err, idx) => (
                                    <div key={idx} className="mb-1">• {err}</div>
                                ))}
                            </div>
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={cerrarModalCarga} disabled={uploading}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ImportarClientes;
